export const cn = (...parts) => parts.filter(Boolean).join(" ");

export const analysisTone = {
  indigo: {
    rail: "before:bg-gradient-to-b before:from-indigo-500 before:to-violet-500",
    eyebrow: "text-indigo-700",
    iconWrap: "bg-indigo-50 text-indigo-600 ring-indigo-100",
    footerPrimary:
      "bg-gradient-to-r from-indigo-50 via-white to-violet-50/80 border-indigo-200 text-indigo-900",
    footerSecondary:
      "bg-gradient-to-r from-emerald-50 via-white to-teal-50/80 border-emerald-200 text-emerald-900",
    accentBorder: "border-indigo-200",
    accentBg: "bg-indigo-50",
    accentText: "text-indigo-700",
  },

  emerald: {
    rail: "before:bg-gradient-to-b before:from-emerald-500 before:to-teal-500",
    eyebrow: "text-emerald-700",
    iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    footerPrimary:
      "bg-gradient-to-r from-emerald-50 via-white to-teal-50/80 border-emerald-200 text-emerald-900",
    footerSecondary:
      "bg-gradient-to-r from-indigo-50 via-white to-violet-50/80 border-indigo-200 text-indigo-900",
    accentBorder: "border-emerald-200",
    accentBg: "bg-emerald-50",
    accentText: "text-emerald-700",
  },

  sky: {
    rail: "before:bg-gradient-to-b before:from-sky-500 before:to-blue-500",
    eyebrow: "text-sky-700",
    iconWrap: "bg-sky-50 text-sky-600 ring-sky-100",
    footerPrimary:
      "bg-gradient-to-r from-sky-50 via-white to-blue-50/80 border-sky-200 text-sky-900",
    footerSecondary:
      "bg-gradient-to-r from-emerald-50 via-white to-teal-50/80 border-emerald-200 text-emerald-900",
    accentBorder: "border-sky-200",
    accentBg: "bg-sky-50",
    accentText: "text-sky-700",
  },

  violet: {
    rail: "before:bg-gradient-to-b before:from-violet-500 before:to-fuchsia-500",
    eyebrow: "text-violet-700",
    iconWrap: "bg-violet-50 text-violet-600 ring-violet-100",
    footerPrimary:
      "bg-gradient-to-r from-violet-50 via-white to-fuchsia-50/80 border-violet-200 text-violet-900",
    footerSecondary:
      "bg-gradient-to-r from-emerald-50 via-white to-teal-50/80 border-emerald-200 text-emerald-900",
    accentBorder: "border-violet-200",
    accentBg: "bg-violet-50",
    accentText: "text-violet-700",
  },
};

export const tableTheme = {
  shell:
    "relative w-full min-w-0 overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-white/96 " +
    "ring-1 ring-white/70 backdrop-blur-[6px] " +
    "shadow-[0_22px_52px_-28px_rgba(15,23,42,0.18)]",

  header:
    "relative w-full overflow-hidden border-b border-slate-200/80 " +
    "bg-[linear-gradient(180deg,rgba(255,255,255,0.99)_0%,rgba(255,255,255,0.985)_58%,rgba(248,250,252,0.94)_100%)] " +
    "px-5 py-4 sm:px-6 sm:py-5",

  headerRail:
    "before:pointer-events-none before:absolute before:left-5 sm:before:left-6 before:top-5 before:bottom-5 before:w-[3px] before:rounded-full",

  headerInner:
    "flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between",

  headerMain:
    "min-w-0 flex-1 pl-4",

  headerAside:
    "flex shrink-0 items-start gap-2.5 self-start lg:justify-end",

  headerAsidePills:
    "flex shrink-0 flex-wrap items-center gap-2 self-start lg:max-w-[38%] lg:justify-end",

  eyebrow:
    "inline-flex items-center gap-1.5 rounded-full border border-slate-200/90 bg-white px-2.5 py-1 " +
    "text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm",

  title:
    "mt-2.5 text-[1.55rem] leading-tight sm:text-[1.7rem] font-semibold tracking-[-0.02em] text-slate-900",

  subtitle:
    "mt-1.5 max-w-[72ch] text-[13px] leading-6 text-slate-600 sm:text-sm",

  toolbarWrap:
    "w-full border-b border-slate-200/75 bg-gradient-to-b from-white to-slate-50/35 px-5 py-4 sm:px-6",

  toolbar:
    "w-full min-w-0 rounded-2xl border border-slate-200/75 bg-white/90 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)]",

  input:
    "h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-700 shadow-sm outline-none " +
    "transition-all duration-200 placeholder:text-slate-400 focus:border-indigo-200 focus:ring-4 focus:ring-indigo-100",

  select:
    "h-11 max-w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 shadow-sm outline-none " +
    "transition-all duration-200 focus:border-indigo-200 focus:ring-4 focus:ring-indigo-100",

  countPill:
    "inline-flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-2 " +
    "text-xs font-medium text-slate-600 shadow-sm ring-1 ring-white/80",

  headerChip:
    "inline-flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-2 " +
    "text-[11px] font-semibold tracking-[0.08em] text-slate-700 shadow-sm",

  headerChipSoft:
    "inline-flex items-center gap-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-2 " +
    "text-[11px] font-semibold tracking-[0.08em] text-slate-600 shadow-sm",

  headerMediaBadge:
    "group relative inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl " +
    "border border-slate-200/80 bg-white/92 shadow-[0_10px_24px_-18px_rgba(15,23,42,0.28)] ring-1 ring-white/80 " +
    "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_30px_-18px_rgba(15,23,42,0.32)]",

  contentInset:
    "px-5 sm:px-6 print:px-0",

  tableBlock:
    "px-5 sm:px-6 pb-5 print:px-0 print:pb-0",

  tableWrap:
    "relative w-full min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain " +
    "rounded-[1.1rem] border border-slate-200/75 bg-white/95 " +
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] " +
    "print:rounded-none print:border-0 print:bg-transparent print:shadow-none",

  table: "w-full text-sm text-left text-slate-700",

  thead:
    "bg-slate-50/95 text-[11px] uppercase tracking-[0.16em] text-slate-500 backdrop-blur-sm",

  th:
    "px-4 sm:px-5 py-3.5 font-semibold border-b border-slate-200/75 align-middle " +
    "whitespace-normal break-words [overflow-wrap:anywhere] leading-[1.15]",

  thInner:
    "flex min-w-0 flex-col gap-0.5",

  thInnerCenter:
    "flex min-w-0 flex-col items-center gap-0.5 text-center",

  thLabel:
    "block max-w-full text-[11px] font-semibold leading-[1.15] whitespace-normal break-words [overflow-wrap:anywhere]",

  thSub:
    "block text-[10px] font-semibold leading-[1.05] normal-case tracking-[0.02em] opacity-90",

  tbody: "divide-y divide-slate-100",

  tr:
    "group/row transition-colors duration-200 hover:bg-slate-50/80",

  td: "px-5 sm:px-6 py-4 align-top",
  tdMuted: "text-slate-600",
  tdStrong: "text-slate-900",
  mono: "font-mono tabular-nums text-slate-900",
  meta: "text-xs text-slate-500",

  empty:
    "mx-5 sm:mx-6 my-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center text-slate-500",

  mobileCard:
    "rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm",

  mobileLabel:
    "block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500",

  detailCard:
    "rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4",

  stickyFade:
    "pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-white via-white/72 to-transparent",
};
